import { useEffect, useState } from "react"
import heroManagedTraffic from "../assets/hero-managed-traffic-square.webp"
import { Link } from "../components/link"
import { SiteHeader } from "../components/site-header"

const heroPhrases = [
  "Add your site. Generate campaigns. Watch leads roll in.",
  "Feed it your site. Launch ads that know what to say.",
  "Turn your website into a campaign machine that ships.",
  "Build the ads. Launch the channels. Catch the demand.",
]

const platformAreas = [
  {
    title: "Add the site",
    body: "Adgiro reads the website, understands the offer, and turns the pages into a campaign-ready brief.",
    items: ["Offer discovery", "Audience context", "Landing pages", "Conversion paths"],
  },
  {
    title: "Generate the assets",
    body: "Click generate and get campaign copy, image directions, media variants, and refresh ideas that feel tied to the brand.",
    items: ["Search copy", "Social variants", "Image concepts", "Creative refreshes"],
  },
  {
    title: "Generate the campaigns",
    body: "Google and Meta campaigns are assembled from the site, connected accounts, creative, budgets, and lead signals.",
    items: ["Google Search", "Performance Max", "Meta traffic", "Lead generation"],
  },
  {
    title: "Let optimization run",
    body: "Leads, spend, creative performance, and campaign changes stay visible while Adgiro keeps improving the account.",
    items: ["Lead feedback", "Spend guardrails", "Approvals", "Optimization history"],
  },
]

const proofPoints = [
  {
    label: "Setup",
    value: "Add site + link accounts",
  },
  {
    label: "Creative",
    value: "Generate all assets",
  },
  {
    label: "Campaigns",
    value: "Generate Google + Meta",
  },
  {
    label: "Optimization",
    value: "Leads feed the loop",
  },
]

const workflow = [
  {
    step: "01",
    title: "Add, link, and tag",
    body: "Add the site, connect Google and Meta, paste the Adgiro tag, and choose the outcome you want more of.",
  },
  {
    step: "02",
    title: "Generate assets and campaigns",
    body: "Click generate all for creative assets, then generate campaigns for Google and Meta launch plans.",
  },
  {
    step: "03",
    title: "Watch leads roll in",
    body: "Lead journeys feed the optimization loop while budgets, approvals, and creative changes stay under control.",
  },
]

const growthStages = [
  {
    title: "Starter",
    body: "For founders and lean teams who need real campaigns without an agency retainer, platform minimums, or a call-us wall.",
    items: ["Self-serve launch", "Site-powered creative", "Lead tracking", "Budget guardrails"],
  },
  {
    title: "Pro",
    body: "For growing teams adding more campaigns, more channels, more creative refreshes, and tighter optimization loops.",
    items: ["More sites", "Team workflows", "Campaign scaling", "Optimization history"],
  },
  {
    title: "Enterprise",
    body: "For organizations that need team-managed systems, stronger account governance, SAML, and deeper operating controls.",
    items: ["SAML", "Teams", "Approvals", "Account governance"],
  },
]

export function HomePage() {
  return (
    <div className="site-frame">
      <SiteHeader />
      <main className="home-layout">
        <section className="hero">
          <div className="hero__copy">
            <div className="eyebrow">Managed growth from your website</div>
            <RotatingHeroHeadline phrases={heroPhrases} />
            <p className="hero__lede">
              Adgiro turns a website into polished Google and Meta campaigns with very little
              effort: link the accounts, paste the tag, generate the assets, generate the campaigns,
              and let optimization keep moving.
            </p>
            <div className="hero__actions">
              <Link href="/login" className="button">
                Start a workspace
              </Link>
              <Link href="/launch" className="button button--ghost">
                Build a public preview
              </Link>
            </div>
          </div>
          <div className="hero__panel">
            <div className="hero-image-frame">
              <img
                src={heroManagedTraffic}
                alt="Adgiro campaign management dashboard with spend, conversions, creative assets, and budget controls"
                className="hero-image"
              />
            </div>
          </div>
        </section>

        <section className="proof-strip" aria-label="Supported advertising operations">
          {proofPoints.map((entry) => (
            <div className="proof-strip__item" key={entry.label}>
              <span>{entry.label}</span>
              <strong>{entry.value}</strong>
            </div>
          ))}
        </section>

        <section className="image-story">
          <div
            className="campaign-desk"
            role="img"
            aria-label="Campaign launch and creative management preview"
          >
            <div className="campaign-desk__header">
              <span>Growth flow</span>
              <strong>Ready to generate</strong>
            </div>
            <div className="campaign-tree">
              <div className="campaign-tree__row campaign-tree__row--active">
                <span>1. Add site</span>
                <strong>Brief created</strong>
              </div>
              <div className="campaign-tree__row">
                <span>2. Link Meta + Google</span>
                <strong>Accounts ready</strong>
              </div>
              <div className="campaign-tree__row">
                <span>3. Paste tag</span>
                <strong>Leads connected</strong>
              </div>
            </div>
            <div className="asset-grid">
              <div className="asset-tile asset-tile--lime">
                <span>Generate all</span>
                <strong>Assets</strong>
              </div>
              <div className="asset-tile asset-tile--white">
                <span>Generate</span>
                <strong>Campaigns</strong>
              </div>
              <div className="asset-tile asset-tile--signal">
                <span>Optimize</span>
                <strong>Leads</strong>
              </div>
            </div>
            <div className="budget-bar">
              <div>
                <span>Budget guardrail</span>
                <strong>Scale only inside your rules</strong>
              </div>
              <div className="budget-bar__track">
                <span />
              </div>
            </div>
          </div>
          <div className="image-story__copy">
            <div className="eyebrow">What Adgiro does</div>
            <h2>The whole launch path, compressed.</h2>
            <p>
              Instead of writing briefs, building asset lists, wiring tracking, and rebuilding
              campaigns across ad platforms, your team works through one focused flow and lets
              Adgiro handle the busywork.
            </p>
            <div className="mini-metrics">
              <div className="mini-metrics__item">
                <span>Start</span>
                <strong>Add site, link Google and Meta, paste the tag</strong>
              </div>
              <div className="mini-metrics__item">
                <span>Create</span>
                <strong>Generate all assets, then generate campaigns</strong>
              </div>
              <div className="mini-metrics__item">
                <span>Grow</span>
                <strong>Lead signals and creative refreshes improve the account</strong>
              </div>
            </div>
          </div>
        </section>

        <section className="workflow-section">
          <div className="section-heading">
            <div className="eyebrow">How it works</div>
            <h2>A simple flow that makes your job easy</h2>
            <p>
              Add a site, link your ad accounts, paste the tag, generate the assets, generate the
              campaigns, then watch leads and optimization start moving.
            </p>
          </div>
          <div className="workflow-grid">
            {workflow.map((item) => (
              <article key={item.step} className="workflow-card">
                <div className="workflow-card__step">{item.step}</div>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="capabilities-section">
          <div className="section-heading">
            <div className="eyebrow">Built for the teams agencies ignore</div>
            <h2>You should not need $100k/month in spend to get serious ad tooling.</h2>
            <p>
              Most campaign systems are locked behind sales calls, minimums, and enterprise buying
              cycles. Adgiro starts where founders and startups actually are, then grows into the
              one platform they need as spend, teams, and campaign complexity increase.
            </p>
          </div>
          <div className="capability-grid">
            {growthStages.map((stage) => (
              <article key={stage.title} className="story-card capability-card">
                <h2>{stage.title}</h2>
                <p>{stage.body}</p>
                <ul className="capability-list">
                  {stage.items.map((entry) => (
                    <li key={entry}>{entry}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="capabilities-section">
          <div className="section-heading">
            <div className="eyebrow">What you get</div>
            <h2>A polished campaign engine, not another ad chore list</h2>
            <p>
              The creative, measurement, optimization, and control customers expect from a modern
              growth team, brought into a simple workspace.
            </p>
          </div>
          <div className="capability-grid">
            {platformAreas.map((item) => (
              <article key={item.title} className="story-card capability-card">
                <h2>{item.title}</h2>
                <p>{item.body}</p>
                <ul className="capability-list">
                  {item.items.map((entry) => (
                    <li key={entry}>{entry}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="docs-callout">
            <div>
              <div className="eyebrow">Product tour</div>
              <h3>See the Adgiro experience</h3>
              <p>
                Walk through the workflow from website intake to creative, launch, measurement, and
                optimization.
              </p>
            </div>
            <Link href="/docs" className="button">
              Explore the flow
            </Link>
          </div>
        </section>
        <footer className="site-footer">
          <div>
            <strong>Adgiro</strong>
            <span>Beautiful managed campaigns from your website.</span>
          </div>
          <nav aria-label="Footer navigation">
            <Link href="/pricing">Pricing</Link>
            <Link href="/privacy">Privacy</Link>
            <Link href="/terms">Terms</Link>
          </nav>
        </footer>
      </main>
    </div>
  )
}

function RotatingHeroHeadline({ phrases }: { phrases: string[] }) {
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    let timeoutId: number | undefined

    const scheduleNextPhrase = () => {
      window.clearTimeout(timeoutId)
      if (document.hidden) return

      timeoutId = window.setTimeout(() => {
        setActiveIndex((currentIndex) => (currentIndex + 1) % phrases.length)
        scheduleNextPhrase()
      }, 6200)
    }

    const handleVisibilityChange = () => {
      scheduleNextPhrase()
    }

    scheduleNextPhrase()
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      window.clearTimeout(timeoutId)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [phrases.length])

  return (
    <h1 className="hero-headline" aria-live="polite">
      {phrases.map((phrase, index) => (
        <span
          className={
            index === activeIndex ? "hero-headline__phrase is-active" : "hero-headline__phrase"
          }
          key={phrase}
          aria-hidden={index !== activeIndex}
        >
          {phrase}
        </span>
      ))}
    </h1>
  )
}
